/**
 * Reads a bank balance typed by a country owner. Spain, France, Portugal and Turkey write the
 * decimal comma ("270.430,82"), Switzerland the apostrophe ("270'430.82"), a phone keyboard may
 * offer either. Balances carry at most two decimals, which decides the ambiguous cases:
 *
 *   - both "." and ","  → the LAST one is the decimal separator, the other groups thousands
 *   - one kind, repeated → thousands ("1.234.567", "1,234,567")
 *   - one kind, once     → decimal if 1–2 digits follow ("270,5", "12.30"),
 *                           thousands if exactly 3 follow ("1,234" = 1234)
 *
 * Returns null for anything else. The caller shows the parsed value next to the field, so a
 * misread is visible before saving.
 */
export function parseBankAmount(raw: string): number | null {
    let s = raw.trim().replace(/[\s  ']/g, '');
    if (!s) return null;
    let sign = 1;
    if (s.startsWith('-') || s.startsWith('−')) { sign = -1; s = s.slice(1); }
    if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return null;

    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');
    let normalized: string;

    if (lastDot >= 0 && lastComma >= 0) {
        const dec = lastDot > lastComma ? '.' : ',';
        const thou = dec === '.' ? ',' : '.';
        const [intPart, frac, ...rest] = s.split(dec);
        if (rest.length || !/^\d{1,2}$/.test(frac)) return null;
        if (!validGroups(intPart, thou)) return null;
        normalized = intPart.split(thou).join('') + '.' + frac;
    } else if (lastDot >= 0 || lastComma >= 0) {
        const sep = lastDot >= 0 ? '.' : ',';
        const parts = s.split(sep);
        if (parts.length > 2) {
            if (!validGroups(s, sep)) return null;
            normalized = parts.join('');
        } else if (/^\d{1,2}$/.test(parts[1])) {
            if (!parts[0]) return null;
            normalized = parts[0] + '.' + parts[1];
        } else if (/^\d{3}$/.test(parts[1]) && /^\d{1,3}$/.test(parts[0])) {
            normalized = parts[0] + parts[1];
        } else {
            return null;
        }
    } else {
        normalized = s;
    }

    const n = Number(normalized);
    if (!Number.isFinite(n)) return null;
    return sign * Math.round(n * 100) / 100;
}

/** "1.234.567" with "." → groups of 3 after a 1–3 digit head. */
function validGroups(s: string, sep: string): boolean {
    const groups = s.split(sep);
    if (groups.length === 1) return /^\d+$/.test(groups[0]);
    return /^\d{1,3}$/.test(groups[0]) && groups.slice(1).every(g => /^\d{3}$/.test(g));
}

/** 270430.82 → "270'430.82" (Swiss grouping, as on the main page). */
export function formatBankAmount(n: number): string {
    return n.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
